"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useRouter } from "next/navigation";

import { useMember } from "@/lib/state/member";
import { trpc } from "@/lib/trpc/client";

import { emitLiveStreamStatus } from "@/lib/realtime/live-stream-status-bus";
import { emitPostReaction } from "@/lib/realtime/post-reaction-bus";

import { useStudioWebSocket, type RealtimeEvent } from "./use-studio-websocket";
import { useOnlineMembers } from "./use-online-members";

interface RealtimeUnreadContextValue {
  isRealtimeConnected: boolean;
  onlineMemberIds: Set<string>;
}

const RealtimeUnreadContext = createContext<RealtimeUnreadContextValue>({
  isRealtimeConnected: false,
  onlineMemberIds: new Set(),
});

export function useRealtimeUnread(): RealtimeUnreadContextValue {
  return useContext(RealtimeUnreadContext);
}

export function useOnlinePresence(): { onlineMemberIds: Set<string> } {
  const { onlineMemberIds } = useContext(RealtimeUnreadContext);
  return { onlineMemberIds };
}

export function RealtimeUnreadProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const { currentMember } = useMember();
  const utils = trpc.useContext();
  const router = useRouter();

  const { onlineMemberIds, markOnline, markOffline } =
    useOnlineMembers(!!currentMember);

  // Coalesce bursts of access-changed broadcasts (one admin save can fire
  // several access mutations back-to-back) into a single RSC refresh.
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleAccessRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    refreshTimeoutRef.current = setTimeout(() => {
      refreshTimeoutRef.current = null;
      router.refresh();
    }, 1000);
  }, [router]);

  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    };
  }, []);

  const handleMessage = useCallback(
    (event: RealtimeEvent) => {
      if (event.action === "post-reaction" && event.postId && event.memberId) {
        emitPostReaction(event.postId, {
          memberId: event.memberId,
          emoji: event.emoji ?? "",
          removed: !!event.removed,
        });
        return;
      }

      if (event.action === "notification") {
        void utils.notifications.unreadCount.invalidate();
        void utils.notifications.list.invalidate();
        return;
      }

      if (event.action === "live-stream-status" && event.eventId) {
        emitLiveStreamStatus(event.eventId, event.status ?? "");
        // CONNECTING (TRA-2805) is transient — nothing in the DB changed, so
        // a refetch would just be wasted requests from every open tab.
        if (event.status === "CONNECTING") return;
        // A Trackstack Live broadcast changed state — refetch the stream
        // through the access-checked procedure so the LIVE badge and player
        // flip within seconds instead of waiting out the 30s poll.
        void utils.liveStreams.get.invalidate({ eventId: event.eventId });
        return;
      }

      if (event.action === "unread") {
        if (event.type === "channel") {
          void utils.channels.unreadCounts.invalidate();
        } else if (event.type === "conversation") {
          void utils.conversations.list.invalidate();
        }
        return;
      }

      if (event.action === "access-changed") {
        // Channel/event access rules changed in this studio (TRA-1890).
        // Refetch cached tRPC queries and re-render the server components
        // (sidebar nav, event lists) so gained/lost access shows up without
        // a hard reload. The channel sidebar lives in a layout-level server
        // component, which Next.js never re-renders on soft navigation —
        // router.refresh() is the only way to update it in-place.
        if (event.type === "channel") {
          void utils.channels.invalidate();
        } else if (event.type === "event") {
          void utils.events.invalidate();
        } else if (event.type === "member") {
          // This member's own effective access changed (cohort membership or
          // membership plan) — that can move them across channel, event AND
          // post gates at once, so refetch everything.
          void utils.invalidate();
        }
        scheduleAccessRefresh();
        return;
      }

      if (event.action === "presence" && event.memberId) {
        if (event.status === "online") markOnline(event.memberId);
        else if (event.status === "offline") markOffline(event.memberId);
      }
    },
    [utils, markOnline, markOffline, scheduleAccessRefresh],
  );

  const { isConnected } = useStudioWebSocket({
    onMessage: handleMessage,
    enabled: !!currentMember,
  });

  const value = useMemo(
    () => ({ isRealtimeConnected: isConnected, onlineMemberIds }),
    [isConnected, onlineMemberIds],
  );

  return (
    <RealtimeUnreadContext.Provider value={value}>
      {children}
    </RealtimeUnreadContext.Provider>
  );
}
